import { Button, Form, Input, Select, Tag } from 'antd'
import { FfsLtaReport } from 'features/indicationTriage/cards/ffsCalc/ffsStrategyContext/ffsReports'
import { ValueSetterPair } from 'features/indicationTriage/cards/ffsCalc/typeUtils'
import { useGeckoPermissions } from 'features/permissions'
import { FC, useCallback, useMemo, useState } from 'react'

import {
  RESULT_OUTCOME_COLORS,
  RESULT_OUTCOME_LABELS,
  useExistingOrganizationScenarios,
} from './ffsLtaPublishApi'
import { FfsResultType } from './ffsTypes'

export const NewSavedReportOutcomeForm: FC<{
  commit: (outcome: FfsLtaReport['outcome']) => void
  currentResult?: FfsResultType
}> = ({ commit, currentResult = FfsResultType.Pass }) => {
  const { user } = useGeckoPermissions()
  const username = user?.name || user?.email || 'Unknown User'

  const [scenario, setScenario] = useState<string>('')
  const [result, setResult] = useState<FfsResultType>(currentResult)
  const [recommendation, setRecommendation] = useState<string>('')
  const [assumptions, setAssumptions] = useState<string>('')
  const [conclusion, setConclusion] = useState<string>('')

  const handleSubmit = useCallback(() => {
    commit({
      result,
      recommendation,
      inspector: username,
      scenario,
      assumptions,
      conclusion,
    })
  }, [
    commit,
    result,
    recommendation,
    username,
    scenario,
    assumptions,
    conclusion,
  ])

  return (
    <Form layout='vertical' onFinish={handleSubmit}>
      <Form.Item
        label='Scenario'
        required
        rules={[
          { required: true, message: 'Please select or enter a scenario' },
        ]}
        validateStatus={scenario ? 'success' : 'error'}>
        <ScenarioSelect scenario={scenario} setScenario={setScenario} />
      </Form.Item>

      <Form.Item label='Outcome' required>
        <OutcomeSelect result={result} setResult={setResult} />
      </Form.Item>

      <Form.Item
        label='Assumptions'
        required
        rules={[
          { required: true, message: 'Please enter assumptions' },
          { min: 1, message: 'Assumptions cannot be empty' },
        ]}
        validateStatus={assumptions.trim() ? 'success' : 'error'}>
        <Input.TextArea
          value={assumptions}
          onChange={(e) => setAssumptions(e.target.value)}
          placeholder='Enter assumptions...'
          rows={3}
        />
      </Form.Item>

      <Form.Item
        label='Conclusion'
        required
        rules={[
          { required: true, message: 'Please enter a conclusion' },
          { min: 1, message: 'Conclusion cannot be empty' },
        ]}
        validateStatus={conclusion.trim() ? 'success' : 'error'}>
        <Input.TextArea
          value={conclusion}
          onChange={(e) => setConclusion(e.target.value)}
          placeholder='Enter conclusion...'
          rows={3}
        />
      </Form.Item>

      <Form.Item
        label='Recommendation'
        required
        rules={[
          { required: true, message: 'Please enter a recommendation' },
          { min: 1, message: 'Recommendation cannot be empty' },
        ]}
        validateStatus={recommendation.trim() ? 'success' : 'error'}>
        <Input.TextArea
          value={recommendation}
          onChange={(e) => setRecommendation(e.target.value)}
          placeholder='Enter recommendation...'
          rows={3}
        />
      </Form.Item>

      <Form.Item>
        <Button
          type='primary'
          htmlType='submit'
          disabled={
            !scenario ||
            !assumptions.trim() ||
            !conclusion.trim() ||
            !recommendation.trim()
          }>
          Save Evaluation
        </Button>
      </Form.Item>
    </Form>
  )
}

export const ScenarioSelect: FC<ValueSetterPair<string, 'scenario'>> = ({
  scenario,
  setScenario,
}) => {
  const { scenarios: orgScenarios, isLoading: isLoadingOrgScenarios } =
    useExistingOrganizationScenarios()

  return (
    <Select
      mode='tags'
      maxCount={1}
      loading={isLoadingOrgScenarios}
      value={scenario ? [scenario] : []}
      onChange={(values) => setScenario(values[0] || '')}
      options={useMemo(
        () =>
          orgScenarios.map((scenario) => ({
            label: scenario,
            value: scenario,
          })),
        [orgScenarios]
      )}
      placeholder='Select existing scenario or type to create new'
      showSearch
      filterOption={(input, option) =>
        option?.value?.toLowerCase().includes(input.toLowerCase()) ?? false
      }
      allowClear
    />
  )
}

export const OutcomeSelect: FC<ValueSetterPair<FfsResultType, 'result'>> = ({
  result,
  setResult,
}) => {
  const options = useMemo(
    () =>
      Object.entries(RESULT_OUTCOME_LABELS).map(([key, label]) => ({
        value: key as FfsResultType,
        label: (
          <Tag color={RESULT_OUTCOME_COLORS[key as FfsResultType]}>{label}</Tag>
        ),
      })),
    []
  )

  return (
    <Select
      value={result}
      onChange={(value) => setResult(value)}
      options={options}
      placeholder='Select outcome'
    />
  )
}
